import { supabase } from '@/integrations/supabase/client';
import { ACCEPTED_DOC_SLUGS, LEGAL_DOCS } from '@/lib/legalDocs';

/**
 * Click-accept audit trail for the legal document set.
 *
 * Consent is given *before* the OAuth redirect (the checkbox on /auth), but it
 * can only be recorded *after* the session exists — so the intent is parked in
 * localStorage across the redirect and flushed on the first authenticated
 * render. The timestamp, user id and email are all set server-side by the
 * `record_legal_acceptance` SECURITY DEFINER function; nothing about the record
 * is client-supplied except the context.
 */
export type LegalAcceptanceContext = 'signup' | 'invite' | 'checkout';

const PENDING_KEY = 'pendingLegalConsent';

export const markPendingLegalConsent = (context: LegalAcceptanceContext) => {
  try {
    localStorage.setItem(PENDING_KEY, context);
  } catch {
    // Storage unavailable (private mode): acceptance is still recorded by the
    // post-auth fallback below, just with the default context.
  }
};

export const recordLegalAcceptance = async (context: LegalAcceptanceContext) => {
  const slugs = ACCEPTED_DOC_SLUGS;
  const versions = slugs.map((slug) => LEGAL_DOCS[slug].version);

  const { error } = await supabase.rpc('record_legal_acceptance', {
    _context: context,
    _doc_slugs: slugs,
    _doc_versions: versions,
  });

  if (error) {
    console.error('[legalAcceptance] Failed to record acceptance:', error.message);
    return false;
  }
  return true;
};

/**
 * Flush a consent captured before sign-in. Safe to call on every authenticated
 * boot: it is a no-op with no parked intent, and the RPC is idempotent per
 * user + document + version.
 */
export const flushPendingLegalConsent = async () => {
  let context: string | null = null;
  try {
    context = localStorage.getItem(PENDING_KEY);
  } catch {
    return;
  }
  if (!context) return;

  // Normalize the legacy client value so consent parked before this contract
  // correction is still recorded after the OAuth redirect.
  const normalizedContext = context === 'invitation' ? 'invite' : context;
  if (!['signup', 'invite', 'checkout'].includes(normalizedContext)) {
    console.error('[legalAcceptance] Invalid pending acceptance context');
    return;
  }

  const ok = await recordLegalAcceptance(normalizedContext as LegalAcceptanceContext);
  if (ok) {
    try {
      localStorage.removeItem(PENDING_KEY);
    } catch {
      // ignore
    }
  }
};